import * as Immutable from 'immutable';
import type { ID, ReduxAction } from '../../types';
import Occasion from '@support/Occasion';
import { AppState } from '@reducers/rootReducer';
import Activity, { ActivityItem, FilteredActivity } from '@models/Activity';
import { BaseActivityState, handleContactActions } from './activity';

export const selectAllGroupActivity = (state: AppState) => state.data.groupActivity.all;
const selectFilteredGroupActivity = (state: AppState) => state.data.groupActivity.filtered;
export const selectActiveGroupActivityFilter = (state: AppState) =>
  state.data.groupActivity.activeFilter;

export const selectGroupActivity = (state: AppState) => {
  if (selectActiveGroupActivityFilter(state)) {
    return selectFilteredGroupActivity(state);
  }
  return selectAllGroupActivity(state);
};

export const selectGroupActivityItem = (activityItemID: ID) => (state: AppState) =>
  state.data.groupActivity.all.items.get(activityItemID) ||
  state.data.groupActivity.filtered.items.get(activityItemID);

export interface GroupActivityState extends BaseActivityState {
  group: ID;
}

const initialGroupActivityState = {
  group: null,
  all: new Activity({ limit: 50 }),
  filtered: new FilteredActivity({ limit: 50 }),
  activeFilter: undefined,
  deletedIDCacheToExpire: Immutable.Set<ID>(),
  mergedIDCacheToExpire: Immutable.Set<ID>(),
};

export function groupActivityReducer(
  state: GroupActivityState = initialGroupActivityState,
  { type, payload }: ReduxAction,
) {
  const targetCollectionKey = payload?.filter ? 'filtered' : 'all';
  const targetCollection = state[targetCollectionKey];
  const otherCollectionKey = targetCollectionKey === 'all' ? 'filtered' : 'all';
  const otherCollection = state[otherCollectionKey];
  const stateChangeFromContactAction = handleContactActions(state, type, payload);

  if (!!stateChangeFromContactAction) {
    return stateChangeFromContactAction;
  }

  switch (type) {
    case 'SET_GROUP_IN_VIEW':
      return {
        ...state,
        group: payload.group?.id || null,
        all: new Activity({ limit: 50 }),
        filtered: new FilteredActivity({ limit: 50 }),
        activeFilter: undefined,
        deletedIDCacheToExpire: Immutable.Set<ID>(),
        mergedIDCacheToExpire: Immutable.Set<ID>(),
      };

    case 'SET_GROUP_ACTIVITY_FILTER':
      return {
        ...state,
        activeFilter: payload.filter,
      };

    case 'FETCH_GROUP_ACTIVITY_REQUESTED':
      return {
        ...state,
        [targetCollectionKey]: targetCollection.copyWith({ isFetching: true }),
      };

    case 'FETCH_GROUP_ACTIVITY_SUCCEEDED':
      const responseCollection = payload.response.results.map((activityItem) => [
        activityItem.id,
        new ActivityItem(activityItem),
      ]);

      // If the request included an LTE date, merge it into the current collection. If it did not
      // include an LTE date, this should be the freshest data in the feed -- overwrite what we've got.
      const items: Immutable.OrderedMap<ID, ActivityItem> =
        targetCollection.updated && payload.resultsAfterDate
          ? targetCollection.items.merge(responseCollection)
          : Immutable.OrderedMap(responseCollection);

      let targetCollectionMergedContactsMap = targetCollection.mergedContactsMap;
      let targetCollectionSourceContactIDToMergedContactIDMap =
        targetCollection.sourceContactIDToMergedContactIDMap;
      let otherCollectionMergedContactsMap = otherCollection.mergedContactsMap;
      let otherCollectionSourceContactIDToMergedContactIDMap =
        otherCollection.sourceContactIDToMergedContactIDMap;

      if (state.deletedIDCacheToExpire.size > 0 || state.mergedIDCacheToExpire.size > 0) {
        state.mergedIDCacheToExpire.forEach((id) => {
          targetCollectionMergedContactsMap = targetCollectionMergedContactsMap.delete(id);
          otherCollectionMergedContactsMap = otherCollectionMergedContactsMap.delete(id);
        });

        // for each of the "source" contact IDs, remove the mapping of that ID to the merged contact ID
        state.deletedIDCacheToExpire.forEach((id) => {
          targetCollectionSourceContactIDToMergedContactIDMap =
            targetCollectionSourceContactIDToMergedContactIDMap.delete(id);
          otherCollectionSourceContactIDToMergedContactIDMap =
            otherCollectionSourceContactIDToMergedContactIDMap.delete(id);
        });
      }

      return {
        ...state,
        [targetCollectionKey]: targetCollection.copyWith({
          isFetching: false,
          updated: new Occasion(),
          items: items.sort((a, b) => b.date.unix - a.date.unix),
          canLoadMore: payload.response.total > payload.response.results.length,
          archivedContactIDs: targetCollection.archivedContactIDs.subtract(
            state.deletedIDCacheToExpire,
          ),
          mergedContactsMap: targetCollectionMergedContactsMap,
          sourceContactIDToMergedContactIDMap: targetCollectionSourceContactIDToMergedContactIDMap,
        }),
        [otherCollectionKey]: otherCollection.copyWith({
          archivedContactIDs: otherCollection.archivedContactIDs.subtract(
            state.deletedIDCacheToExpire,
          ),
          mergedContactsMap: otherCollectionMergedContactsMap,
          sourceContactIDToMergedContactIDMap: otherCollectionSourceContactIDToMergedContactIDMap,
        }),
        deletedIDCacheToExpire: Immutable.Set(),
        mergedIDCacheToExpire: Immutable.Set(),
      };

    case 'FETCH_GROUP_ACTIVITY_FAILED':
    default:
      return state;
  }
}
